import { useNavigate, useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Dialog, DialogContent, DialogTitle, ButtonGroup, Tooltip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useGetItemQuery, useDeletePropertyMutation, useUpdatePropertyMutation } from '../store/docApi';
import ColorChart from '../components/charts/ColorChart';
import ColorChartThumbnail from '../components/charts/ColorChartThumbnail';
import EditableProperty from '../components/widgets/EditableProperty';
import ColorwaySelector from '../Colorway/ColorwaySelector';
import { DoubleKnitChart } from '../components/charts/DoubleKnitChart';
import KnitChart from '../components/charts/KnitChart';
import ChartEditor from '../Chart/ChartEditor';
import ColorThumbnailControls from '../Chart/ColorThumbnailControls';
import ColorThumbnail from '../Chart/ColorThumbnail';
import ChartViewer from '../components/charts/editor/ChartViewer';

export default function MotifDetails() {
  const params = useParams();
  const subId = params.subId;
  const [motif, setMotif] = useState(null);
  const { data: item } = useGetItemQuery({ label: 'workspace', pk: params.pk, id: params.id });
  const [deleteProperty] = useDeletePropertyMutation();
  const [updateProperty] = useUpdatePropertyMutation();
  const navigate = useNavigate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [colors, setColors] = useState(['#ffffff', '#d3d3d3', '#808080', '#3d3d3d']);
  const [selectedColorway, setSelectedColorway] = useState(null);
  const [chartEditorOpen, setChartEditorOpen] = useState(false);
  const [thumbnailControls, setThumbnailControls] = useState({
    width: 120,
    height: 90,
    view: 'rough',
    cellRatio: '3:4',
    showGrid: false
  });

  useEffect(() => {
    if (item && item.motifs && item.motifs[subId]) {
      setMotif(item.motifs[subId]);
      if (item.motifs[subId].colors) setColors(item.motifs[subId].colors);
    }
  }, [item, subId]);

  function deleteMotif() {
    setDeleteConfirmOpen(false);
    deleteProperty({ pk: item.pk, id: item.id, name: `motifs/${subId}` });
    navigate(`/workspaces/${item.pk}/${item.id}`);
  }

  const selectColorway = (colorway) => {
    if (colorway.chromas && colorway.chromas.length > 0) {
      const colors = colorway.chromas.map((chroma) => chroma.color);
      if (colors.length > 0) {
        setColors(colors);
      }
      setSelectedColorway(colorway);
    }
  };

  // Utility: extract chart properties from motif
  function getMotifChartProps(motif) {
    return {
      knitdata: motif.knitdata || null,
      verticalLines: motif.verticalLines || [],
      colordata: motif.colordata || motif.data || null,
      colors: motif.colors || null,
      icons: motif.icons || null,
    };
  }

  // Utility: save chart properties to motif
  async function saveMotifChartProps({ pk, id, subId, chartProps }) {
    const updates = [];
    for (const [key, value] of Object.entries(chartProps)) {
      updates.push(updateProperty({
        pk,
        id,
        name: `motifs/${subId}/${key}`,
        value
      }));
    }
    await Promise.all(updates);
  }

  // Save handler for ChartEditor
  const handleChartSave = async (newChartProps) => {
    await saveMotifChartProps({
      pk: item.pk,
      id: item.id,
      subId,
      chartProps: newChartProps
    });
    setChartEditorOpen(false);
  };

  if (item && motif && motif.doubleknitchart) {
    if (motif.steps && motif.steps.length >= 2) {
      return <DoubleKnitChart data1={motif.steps[0].data} data2={motif.steps[1].data} />;
    }
  }

  return (
    item &&
    motif && (
      <Box key={motif.id}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {motif.name && (
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {motif.name}
            </Typography>
          )}
          <ButtonGroup variant="outlined" size="small">
            <Tooltip title="Edit Motif">
              <Button startIcon={<EditIcon />} onClick={() => navigate(`/workspaces/${item.pk}/${item.id}/motifs/${subId}/edit`)}>
                Edit
              </Button>
            </Tooltip>
            <Tooltip title="Delete Motif">
              <Button startIcon={<DeleteIcon />} color="error" onClick={() => setDeleteConfirmOpen(true)}>
                Delete
              </Button>
            </Tooltip>
          </ButtonGroup>
        </Box>
        <EditableProperty
          item={item}
          title="Name"
          propname={`motifs/${subId}/name`}
          propvalue={motif.name}
          placeholder="Enter motif name..."
        />
        <EditableProperty
          item={item}
          title="Description"
          propname={`motifs/${subId}/description`}
          propvalue={motif.description}
          placeholder="Enter motif description..."
        />
        <EditableProperty
          item={item}
          title="Keywords"
          propname={`motifs/${subId}/keywords`}
          propvalue={motif.keywords}
          placeholder="Enter motif keywords..."
        />
        {motif.author && <Typography variant="body2">Designed by {motif.author}</Typography>}
        {motif.cols > 1 && (
          <Typography variant="body2">
            Multiple of {motif.cols} sts {motif.extracols && <span>plus {motif.extracols}</span>}
          </Typography>
        )}
        <ColorwaySelector item={item} onSelect={selectColorway} />
        <Button 
          variant="outlined" 
          sx={{ mb: 2 }} 
          onClick={() => setChartEditorOpen(true)}
        >
          {(motif.knitdata || motif.colordata || motif.data) ? 'Edit Chart' : 'Create Chart'}
        </Button>
        
        {/* Chart Preview Section - Show chart preview if data exists */}
        {(motif.knitdata || motif.colordata || motif.data) && (
          <ChartViewer
            motif={motif}
            colors={colors}
            selectedColorway={selectedColorway}
            title="Interactive Chart"
            maxWidth={600}
          />
        )}
        
        {motif.data && (
          <Box sx={{ mt: 3 }}>
            <ColorChartThumbnail data={motif.data} colors={colors} mincols={96} norepeat={motif.norepeat} settings={motif.settings} />
          </Box>
        )}
        {false && <ColorChart item={motif} chartid={motif.id} colorway={selectedColorway} viewonly={true} />}
        {motif.steps && motif.knitchart && (
          <KnitChart steps={motif.steps} chartid={motif.id}/>
        )}
        
        {/* Color Thumbnail and Controls (interactive, like Playground) */}
        {<Box sx={{ mt: 2, mb: 2 }}>
          <ColorThumbnailControls
            colordata={motif.colordata || motif.data}
            colors={colors}
            thumbnailWidth={thumbnailControls.width}
            thumbnailHeight={thumbnailControls.height}
            thumbnailView={thumbnailControls.view}
            thumbnailCellRatio={thumbnailControls.cellRatio}
            thumbnailShowGrid={thumbnailControls.showGrid}
            onWidthChange={value => setThumbnailControls(prev => ({ ...prev, width: value }))}
            onHeightChange={value => setThumbnailControls(prev => ({ ...prev, height: value }))}
            onViewChange={value => setThumbnailControls(prev => ({ ...prev, view: value }))}
            onCellRatioChange={value => setThumbnailControls(prev => ({ ...prev, cellRatio: value }))}
            onShowGridChange={checked => setThumbnailControls(prev => ({ ...prev, showGrid: checked }))}
            title="Motif Thumbnail Preview"
            layout="horizontal"
            settings={motif.settings}
          />
          <Box sx={{ mt: 2 }}>
            <ColorThumbnail
              colordata={motif.colordata || motif.data}
              colors={colors}
              width={thumbnailControls.width}
              height={thumbnailControls.height}
              view={thumbnailControls.view}
              cellRatio={thumbnailControls.cellRatio}
              showGrid={thumbnailControls.showGrid}
              settings={motif.settings}
            />
          </Box>
        </Box>}

        {motif.sources &&
          motif.sources.map((source, i) => (
            <Typography key={i} variant="body2">
              {source.id} {source.chapter} {source.page}
            </Typography>
          ))}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} fullWidth>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>Delete this motif from {item.name}?</Typography>
          </DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, p: 2 }}>
            <Button variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deleteMotif}>
              Delete
            </Button>
          </Box>
        </Dialog>
        <Dialog open={chartEditorOpen} onClose={() => setChartEditorOpen(false)} fullScreen >
          <DialogTitle>Edit Motif Chart</DialogTitle>
          <DialogContent>
            <ChartEditor
              {...getMotifChartProps(motif)}
              onSave={handleChartSave}
              onCancel={() => setChartEditorOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </Box>
    )
  );
}
